import type { ReactNode } from "react";
import { ChevronLeft, ChevronRight, Loader2, X } from "lucide-react";

import { CarrierLogo } from "@/components/common/CarrierLogo";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import type { ScheduleResponse } from "@/types/api";
import { formatBookingDate } from "@/types/booking";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MAX_CHIPS = 3;

function dateKey(iso: string | null | undefined): string | null {
  return iso ? iso.slice(0, 10) : null;
}

export function sailingEtd(s: ScheduleResponse): string | null {
  return dateKey(s.origin_estimated_date ?? s.legs[0]?.departure_estimated_date);
}

export function sailingEta(s: ScheduleResponse): string | null {
  const last = s.legs[s.legs.length - 1];
  return dateKey(s.dest_estimated_date ?? last?.arrival_estimated_date);
}

export function vesselName(s: ScheduleResponse): string {
  return s.legs[0]?.vessel_name?.trim() || "—";
}

export function carrierCode(s: ScheduleResponse): string {
  return s.carrier_canonical_code || s.carrier_name;
}

interface ScheduleLookupCalendarDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  month: Date;
  onShiftMonth: (delta: number) => void;
  sailings: ScheduleResponse[];
  selectedDay: string | null;
  onSelectDay: (iso: string | null) => void;
  onSelectSailing: (sailing: ScheduleResponse) => void;
  loading?: boolean;
  /** Keep the calendar open while a nested sailing-detail dialog is showing. */
  holdOpen?: boolean;
  laneLabel?: string;
  renderSailing: (sailing: ScheduleResponse) => ReactNode;
}

function monthCells(month: Date): { iso: string; inMonth: boolean }[] {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const mondayOffset = (first.getDay() + 6) % 7;
  const start = new Date(first);
  start.setDate(first.getDate() - mondayOffset);
  return Array.from({ length: 42 }, (_, i) => {
    const day = new Date(start);
    day.setDate(start.getDate() + i);
    const iso = [
      day.getFullYear(),
      String(day.getMonth() + 1).padStart(2, "0"),
      String(day.getDate()).padStart(2, "0"),
    ].join("-");
    return { iso, inMonth: day.getMonth() === month.getMonth() };
  });
}

/**
 * Month calendar for Schedule Lookup. Each day can list several vessels;
 * clicking a chip or a day row opens the sailing detail.
 */
export function ScheduleLookupCalendarDialog({
  open,
  onOpenChange,
  month,
  onShiftMonth,
  sailings,
  selectedDay,
  onSelectDay,
  onSelectSailing,
  loading = false,
  holdOpen = false,
  laneLabel,
  renderSailing,
}: ScheduleLookupCalendarDialogProps) {
  const cells = monthCells(month);
  const monthLabel = month.toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
  });

  const byDay = new Map<string, ScheduleResponse[]>();
  for (const sailing of sailings) {
    const key = sailingEtd(sailing);
    if (!key) continue;
    const list = byDay.get(key) ?? [];
    list.push(sailing);
    byDay.set(key, list);
  }

  const daySailings = selectedDay ? (byDay.get(selectedDay) ?? []) : [];
  const todayIso = (() => {
    const d = new Date();
    return [
      d.getFullYear(),
      String(d.getMonth() + 1).padStart(2, "0"),
      String(d.getDate()).padStart(2, "0"),
    ].join("-");
  })();

  function preventWhileHeld(event: { preventDefault: () => void }) {
    if (holdOpen) event.preventDefault();
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="glass-overlay flex max-h-[min(90dvh,var(--app-vvh,90vh))] w-[min(52rem,calc(100vw-1.5rem))] flex-col overflow-hidden rounded-2xl border-border/60 p-0 shadow-2xl"
        aria-describedby={undefined}
        onPointerDownOutside={preventWhileHeld}
        onInteractOutside={preventWhileHeld}
        onFocusOutside={preventWhileHeld}
      >
        <div className="flex shrink-0 items-start gap-3 border-b border-border/50 px-5 py-4">
          <DialogHeader className="min-w-0 flex-1 space-y-1 text-left">
            <DialogTitle className="text-base font-semibold leading-tight">
              Sailing calendar
            </DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              {laneLabel || "Upcoming sailings by departure date."}
              {sailings.length > 0 && (
                <span>
                  {" "}
                  · {sailings.length} sailing{sailings.length === 1 ? "" : "s"}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogClose
            className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-foreground/10 hover:text-foreground"
            aria-label="Close"
          >
            <X className="size-4" />
          </DialogClose>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          <div className="flex items-center justify-between gap-2">
            <button
              type="button"
              aria-label="Previous month"
              className="flex size-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent/60 hover:text-foreground"
              onClick={() => onShiftMonth(-1)}
            >
              <ChevronLeft className="size-4" />
            </button>
            <span className="flex items-center gap-2 text-sm font-semibold">
              {monthLabel}
              {loading && (
                <Loader2 className="size-3.5 animate-spin text-muted-foreground" />
              )}
            </span>
            <button
              type="button"
              aria-label="Next month"
              className="flex size-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent/60 hover:text-foreground"
              onClick={() => onShiftMonth(1)}
            >
              <ChevronRight className="size-4" />
            </button>
          </div>

          <div className="mt-3 grid grid-cols-7 gap-px">
            {WEEKDAYS.map((day) => (
              <div
                key={day}
                className="pb-1 text-center text-[10px] font-medium uppercase text-muted-foreground"
              >
                <span className="sm:hidden">{day.slice(0, 1)}</span>
                <span className="hidden sm:inline">{day}</span>
              </div>
            ))}
            {cells.map((cell) => {
              const daySailingsForCell = byDay.get(cell.iso) ?? [];
              const selected = selectedDay === cell.iso;
              const isToday = cell.iso === todayIso;
              return (
                <div
                  key={cell.iso}
                  className={cn(
                    "flex min-h-14 flex-col gap-0.5 rounded-md p-1 sm:min-h-[5.5rem]",
                    !cell.inMonth && "opacity-30",
                    selected && "bg-primary/12",
                    cell.inMonth && !selected && "hover:bg-accent/40",
                  )}
                >
                  <button
                    type="button"
                    disabled={!cell.inMonth}
                    onClick={() =>
                      onSelectDay(selected ? null : cell.iso)
                    }
                    className={cn(
                      "self-start rounded-full px-1.5 text-[11px] tabular-nums",
                      isToday && "bg-primary/15 font-semibold text-primary",
                      !isToday && daySailingsForCell.length > 0 && "font-semibold",
                    )}
                  >
                    {Number(cell.iso.slice(8, 10))}
                  </button>
                  {cell.inMonth && daySailingsForCell.length > 0 && (
                    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <div className="flex flex-wrap items-center gap-0.5 sm:hidden">
                        {daySailingsForCell.slice(0, MAX_CHIPS).map((sailing) => (
                          <button
                            key={sailing.id}
                            type="button"
                            title={vesselName(sailing)}
                            className="rounded-sm p-0.5 hover:bg-background/70"
                            onClick={() => {
                              onSelectDay(cell.iso);
                              onSelectSailing(sailing);
                            }}
                          >
                            <CarrierLogo
                              carrier={carrierCode(sailing)}
                              logoOnly
                              size={12}
                            />
                          </button>
                        ))}
                        {daySailingsForCell.length > MAX_CHIPS && (
                          <span className="text-[9px] text-muted-foreground">
                            +{daySailingsForCell.length - MAX_CHIPS}
                          </span>
                        )}
                      </div>
                      <div className="hidden min-w-0 flex-col gap-0.5 sm:flex">
                        {daySailingsForCell.slice(0, MAX_CHIPS).map((sailing) => (
                          <button
                            key={sailing.id}
                            type="button"
                            className="flex min-w-0 items-center gap-1 rounded px-0.5 py-0.5 text-left hover:bg-background/70"
                            onClick={() => {
                              onSelectDay(cell.iso);
                              onSelectSailing(sailing);
                            }}
                          >
                            <CarrierLogo
                              carrier={carrierCode(sailing)}
                              logoOnly
                              size={11}
                            />
                            <span className="min-w-0 flex-1 truncate text-[10px] leading-tight">
                              {vesselName(sailing)}
                            </span>
                          </button>
                        ))}
                        {daySailingsForCell.length > MAX_CHIPS && (
                          <button
                            type="button"
                            className="px-0.5 text-left text-[10px] text-muted-foreground hover:text-foreground"
                            onClick={() => onSelectDay(cell.iso)}
                          >
                            +{daySailingsForCell.length - MAX_CHIPS} more
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {selectedDay && (
            <div className="mt-4 border-t border-border/40 pt-3">
              <p className="mb-2 text-xs font-semibold text-muted-foreground">
                {formatBookingDate(selectedDay)}
                {daySailings.length > 0 && (
                  <span>
                    {" "}
                    · {daySailings.length} vessel
                    {daySailings.length === 1 ? "" : "s"}
                  </span>
                )}
              </p>
              {daySailings.length === 0 ? (
                <p className="px-1 py-2 text-center text-[11px] text-muted-foreground">
                  No sailings on this day
                </p>
              ) : (
                <ul className="flex flex-col gap-0.5">
                  {daySailings.map((sailing) => renderSailing(sailing))}
                </ul>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
